import { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Spinner } from './ui/Spinner';
import { ConfirmModal } from './ui/Modal';
import {
  getEmailForwardingStatus,
  addEmailForward,
  removeEmailForward,
  disableEmailRouting,
  enableEmailRouting,
  ForwardingRule,
} from '../api/email-forwarding';

/**
 * Staff email-forwarding management for a site's domain. Lists live rules
 * (reconciled — manual/external rules surfaced too), adds forwards, and removes
 * them, all via the /api/admin/email-forwarding/* endpoints. Cloudflare is the
 * source of truth; this is a thin UI over the same engine the CLI uses.
 */
export function EmailForwardingManager({ domain }: { domain: string | null }) {
  const [rules, setRules] = useState<ForwardingRule[]>([]);
  const [routing, setRouting] = useState<{ enabled: boolean | null; status: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [localPart, setLocalPart] = useState('');
  const [destination, setDestination] = useState('');
  const [adding, setAdding] = useState(false);

  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const [confirmDisable, setConfirmDisable] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const load = async () => {
    if (!domain) {
      setLoading(false);
      return;
    }
    try {
      const res = await getEmailForwardingStatus(domain);
      // Drop empty-address rules (e.g. the catch-all drop rule) — not forwards.
      setRules((res.rules || []).filter((r) => r.address));
      setRouting(res.routing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forwarding');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain) return;
    const lp = localPart.trim().toLowerCase();
    const dest = destination.trim().toLowerCase();
    if (!lp || !dest) return;
    setAdding(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await addEmailForward(domain, lp, dest);
      setSuccess(res.summary);
      setLocalPart('');
      setDestination('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add forwarding');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!domain || !confirmRemove) return;
    const lp = confirmRemove.split('@')[0];
    setRemoving(true);
    setError(null);
    setSuccess(null);
    try {
      await removeEmailForward(domain, lp);
      setSuccess(`Removed ${confirmRemove}`);
      setConfirmRemove(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove forwarding');
    } finally {
      setRemoving(false);
    }
  };

  const handleDisable = async () => {
    if (!domain) return;
    setDisabling(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await disableEmailRouting(domain);
      setSuccess(res.summary);
      setConfirmDisable(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable routing');
    } finally {
      setDisabling(false);
    }
  };

  const handleEnable = async () => {
    if (!domain) return;
    setEnabling(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await enableEmailRouting(domain);
      setSuccess(res.summary);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-enable routing');
    } finally {
      setEnabling(false);
    }
  };

  const suspendedCount = rules.filter((r) => r.status === 'suspended').length;
  const routingDisabled = routing?.enabled === false;

  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Email forwarding</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create forwarding addresses on{' '}
            {domain ? <span className="font-mono">{domain}</span> : 'this domain'} (e.g.{' '}
            <span className="font-mono">info@…</span> → an inbox).
          </p>
        </div>
        {routing && (
          <Badge variant={routing.enabled === false ? 'warning' : 'info'}>
            routing:{' '}
            {routing.enabled == null ? 'unknown' : routing.enabled ? 'enabled' : 'disabled'}
          </Badge>
        )}
      </div>

      {!domain ? (
        <p className="mt-4 text-sm text-gray-500">
          Set a custom domain for this site first — forwarding needs a domain on Cloudflare.
        </p>
      ) : (
        <>
          {error && (
            <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}
          {success && (
            <div className="mt-3 p-3 bg-green-50 text-green-700 rounded-lg text-sm">{success}</div>
          )}

          {/* Routing paused: offer restore. If nothing was saved, it's a fresh
              domain — hint that adding an address turns routing on. */}
          {!loading && routingDisabled && suspendedCount > 0 && (
            <div className="mt-3 p-3 bg-amber-50 rounded-lg flex items-start justify-between gap-3">
              <div className="text-sm">
                <p className="font-medium text-amber-800">Email routing is paused</p>
                <p className="mt-0.5 text-amber-700">
                  {suspendedCount} saved address{suspendedCount === 1 ? '' : 'es'} will be restored
                  when you re-enable.
                </p>
              </div>
              <Button size="sm" loading={enabling} onClick={handleEnable} className="shrink-0">
                Re-enable routing
              </Button>
            </div>
          )}
          {!loading && routingDisabled && suspendedCount === 0 && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              Email routing is off for this domain. Add an address below to turn it on.
            </div>
          )}

          <div className="mt-4">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Loading…
              </div>
            ) : rules.length === 0 ? (
              <p className="text-sm text-gray-500">No forwarding addresses yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {rules.map((r) => {
                  const state = r.status || (r.enabled ? 'active' : 'disabled');
                  return (
                    <li key={r.address} className="flex items-start justify-between gap-2 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm font-medium text-gray-900 break-all">
                          {r.address}
                        </div>
                        <div className="font-mono text-xs text-gray-500 break-all">
                          → {r.destination || '—'}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <Badge
                            variant={
                              state === 'active'
                                ? 'success'
                                : state === 'pending-verification' || state === 'suspended'
                                  ? 'warning'
                                  : 'default'
                            }
                          >
                            {state}
                          </Badge>
                          <Badge variant={r.managed ? 'info' : 'default'}>
                            {r.managed ? 'managed' : 'external'}
                          </Badge>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setConfirmRemove(r.address)}
                        className="shrink-0 text-xs font-medium text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <form onSubmit={handleAdd} className="mt-4 space-y-3 border-t border-gray-100 pt-4">
            <div>
              <Input
                label="Address"
                placeholder="info"
                value={localPart}
                onChange={(e) => setLocalPart(e.target.value)}
              />
              <p className="mt-1 font-mono text-xs text-gray-400 break-all">…@{domain}</p>
            </div>
            <Input
              label="Forward to"
              type="email"
              placeholder="inbox@example.com"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
            <Button
              type="submit"
              size="md"
              loading={adding}
              disabled={!localPart.trim() || !destination.trim()}
            >
              Add forwarding
            </Button>
            <p className="text-xs text-gray-400">
              A new destination inbox must confirm Cloudflare's verification email before mail
              forwards.
            </p>
          </form>

          {/* Danger zone — domain-wide pause, kept separate from per-address Remove. */}
          {routing?.enabled === true && (
            <div className="mt-5 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-400">
                Turn off email forwarding for this whole domain. Saved addresses are restored if you
                re-enable.
              </p>
              <button
                type="button"
                onClick={() => setConfirmDisable(true)}
                className="shrink-0 text-xs font-medium text-red-600 hover:text-red-700"
              >
                Disable routing
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={handleRemove}
        title="Remove forwarding address"
        message={`Stop forwarding ${confirmRemove ?? ''}? This deletes the Cloudflare routing rule.`}
        confirmText="Remove"
        confirmVariant="danger"
        loading={removing}
      />

      <ConfirmModal
        isOpen={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        onConfirm={handleDisable}
        title="Disable email routing"
        message={`Turn OFF email routing for ${domain ?? 'this domain'}? Forwarding stops for every address on this domain. The current addresses are saved and restored when you re-enable — nothing is permanently deleted.`}
        confirmText="Disable routing"
        confirmVariant="danger"
        loading={disabling}
      />
    </Card>
  );
}
